import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  Skeleton,
  Switch,
  Typography,
} from '@mui/material';
import { IDeliveryMethodsState, IRootState } from '../../types/store';
import { setSetting } from '../../store/Reducers/SettingsSlice';
import { showNotification } from '../../store/Reducers/NotificationSnackbarSlice';
import { SettingsService } from '../../api/services/settingsService';

const PAYMENT_METHODS: { key: keyof IDeliveryMethodsState; label: string; description: string }[] =
  [
    { key: 'cod', label: 'COD', description: 'Cash on delivery payments' },
    { key: 'mpaisa', label: 'MPaisa', description: 'MPaisa digital wallet payments' },
    { key: 'mycash', label: 'MyCash', description: 'MyCash digital wallet payments' },
    { key: 'bsp', label: 'BSP', description: 'BSP payments' },
  ];

const ALL_KEYS = PAYMENT_METHODS.map((m) => m.key);

export const SettingsPage = () => {
  const dispatch = useDispatch();
  const settings = useSelector((state: IRootState) => state.settings);
  const [isLoading, setIsLoading] = useState(true);
  const [updating, setUpdating] = useState<keyof IDeliveryMethodsState | null>(null);

  useEffect(() => {
    const fetchAll = async () => {
      setIsLoading(true);
      try {
        const results = await Promise.all(
          ALL_KEYS.map((k) => SettingsService.getSettingByKeyname(k)),
        );
        results.forEach((res, i) => {
          dispatch(setSetting({ key: ALL_KEYS[i], value: res.metavalue === 'open' }));
        });
      } catch {
        dispatch(
          showNotification({
            message: 'Failed to load settings. Please reload the page.',
            severity: 'error',
          }),
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchAll();
  }, [dispatch]);

  const handleToggle = async (key: keyof IDeliveryMethodsState) => {
    const newValue = !settings[key];
    setUpdating(key);
    try {
      await SettingsService.updateSettingValue(key, newValue ? 'open' : 'close');
      dispatch(setSetting({ key, value: newValue }));
    } catch {
      dispatch(showNotification({ message: 'Failed to update setting.', severity: 'error' }));
    } finally {
      setUpdating(null);
    }
  };

  const renderSection = (
    title: string,
    methods: { key: keyof IDeliveryMethodsState; label: string; description: string }[],
  ) => (
    <Paper elevation={1} sx={{ mb: 3, maxWidth: 560 }}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ px: 2, pt: 2, pb: 1 }}>
        {title}
      </Typography>
      <Divider />
      <List disablePadding>
        {methods.map(({ key, label, description }, i) => (
          <React.Fragment key={key}>
            <ListItem
              secondaryAction={
                isLoading ? (
                  <Skeleton variant="rounded" width={48} height={24} />
                ) : (
                  <Switch
                    checked={settings[key]}
                    onChange={() => handleToggle(key)}
                    disabled={updating !== null}
                  />
                )
              }
            >
              {isLoading ? (
                <ListItemText
                  primary={<Skeleton width={80} />}
                  secondary={<Skeleton width={160} />}
                />
              ) : (
                <ListItemText primary={label} secondary={description} />
              )}
            </ListItem>
            {i < methods.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </List>
    </Paper>
  );

  return <Box>{renderSection('Payment Methods', PAYMENT_METHODS)}</Box>;
};
